import { describeDomains, noArgsSchema } from "../operation-meta.js";
import { ForbiddenError, NotFoundError } from "../../errors.js";
import { isStaff, scopeToReadableProjects } from "../project-access.js";
import { assertProjectWriteAccess, assertSkillWriteAccess, normalizeZeroDateArray, syncProjectFunctionSeriesDays, toZeroDate, toZeroDateArray, } from "../projects-helpers.js";
import { createDefaultFunctionGroupInputSchema, createProjectFunctionGroupInputSchema, defaultFunctionGroupIDInputSchema, deleteProjectFunctionGroupInputSchema, deleteProjectPersonTravelDayInputSchema, updateDefaultFunctionGroupInputSchema, updateProjectFunctionGroupInputSchema, upsertProjectPersonTravelDayInputSchema, personIDQueryArgsSchema, personIDsQueryArgsSchema, projectIDQueryArgsSchema, skillRatingsAtLeastQueryArgsSchema, } from "../projects-input-schemas.js";
function authorizedDefaultFunctionsQuery(ctx, projectZql) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.defaultFunctions.where("id", "__unauthorized__");
    }
    return projectZql.defaultFunctions;
}
function authorizedDefaultFunctionGroupsQuery(ctx, projectZql) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.defaultFunctionGroups.where("id", "__unauthorized__");
    }
    return projectZql.defaultFunctionGroups;
}
/**
 * Staff see every row. Freelancers see their own rows plus every row of the
 * projects they collaborate on; other users only their own rows.
 */
function scopeToReadableProjectsOrOwnRows(ctx, query, inProjects) {
    if (isStaff(ctx))
        return query;
    const personID = ctx.personID;
    if (ctx.role === "freelancer") {
        if (ctx.clientSide)
            return query;
        const projectIDs = [...(ctx.readableProjectIDs ?? [])];
        if (projectIDs.length > 0) {
            return query.where((eb) => personID
                ? eb.or(eb.cmp("personID", personID), inProjects(eb, projectIDs))
                : inProjects(eb, projectIDs));
        }
    }
    return personID
        ? query.where("personID", personID)
        : query.where("id", "__unauthorized__");
}
const projectIDIn = (eb, projectIDs) => eb.cmp("projectID", "IN", projectIDs);
function authorizedProjectFunctionGroupsQuery(ctx, projectZql) {
    return scopeToReadableProjects(ctx, projectZql.projectFunctionGroups);
}
function authorizedProjectPersonTravelDaysQuery(ctx, projectZql) {
    return scopeToReadableProjectsOrOwnRows(ctx, projectZql.projectPersonTravelDays, projectIDIn);
}
function authorizedSkillRatingsQuery(ctx, projectZql) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.personSkillRatings.where("id", "__unauthorized__");
    }
    return projectZql.personSkillRatings;
}
function authorizedProjectAvailabilityQuery(ctx, projectZql) {
    return scopeToReadableProjectsOrOwnRows(ctx, projectZql.projectAvailability, projectIDIn);
}
function authorizedProjectFunctionSeriesQuery(ctx, projectZql) {
    return scopeToReadableProjects(ctx, projectZql.projectFunctionSeries);
}
function authorizedFunctionAssignmentsQuery(ctx, projectZql) {
    return scopeToReadableProjectsOrOwnRows(ctx, projectZql.functionAssignments, (eb, projectIDs) => eb.exists("projectFunction", (projectFunction) => projectFunction.where("projectID", "IN", projectIDs)));
}
function authorizedProjectCollaboratorsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.projectCollaborators;
    }
    return ctx.personID
        ? projectZql.projectCollaborators.where("personID", ctx.personID)
        : projectZql.projectCollaborators.where("id", "__unauthorized__");
}
function assertProjectCollaboratorManagementAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new ForbiddenError("Not allowed to manage project collaborators");
    }
}
const COLLABORATOR_PERSON_TYPES = ["employee", "freelancer"];
/** Rejects ids of rows that belong to another project than `projectID`. */
async function assertProjectRowWriteAccess(tx, ctx, projectZql, table, id, projectID) {
    await assertProjectWriteAccess(tx, ctx, projectID);
    if (isStaff(ctx))
        return;
    const row = await tx.run(projectZql[table].where("id", id).one());
    if (!row || row.projectID !== projectID) {
        throw new NotFoundError("Not found");
    }
}
async function assertFunctionAssignmentWriteAccess(tx, ctx, projectZql, id, projectID) {
    await assertProjectWriteAccess(tx, ctx, projectID);
    if (isStaff(ctx))
        return;
    const assignment = await tx.run(projectZql.functionAssignments.where("id", id).one());
    if (!assignment)
        throw new NotFoundError("Function assignment not found");
    await assertProjectFunctionsInProject(tx, ctx, projectZql, projectID, [
        assignment.functionID,
    ]);
}
async function assertProjectFunctionsInProject(tx, ctx, projectZql, projectID, functionIDs) {
    if (isStaff(ctx))
        return;
    for (const functionID of functionIDs) {
        const projectFunction = await tx.run(projectZql.projectFunctions.where("id", functionID).one());
        if (!projectFunction || projectFunction.projectID !== projectID) {
            throw new NotFoundError("Project function not found");
        }
    }
}
async function assertProjectCollaboratorInProject(tx, projectZql, id, projectID) {
    const collaborator = await tx.run(projectZql.projectCollaborators.where("id", id).one());
    if (!collaborator || collaborator.projectID !== projectID) {
        throw new NotFoundError("Project collaborator not found");
    }
}
export function createPeoplePlanningQueries({ defineProjectQuery, projectZql, }) {
    return {
        defaultFunctions: {
            list: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedDefaultFunctionsQuery(ctx, projectZql)
                .related("defaultFunctionGroup")
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc")),
        },
        defaultFunctionGroups: {
            list: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedDefaultFunctionGroupsQuery(ctx, projectZql)
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc")),
        },
        personSkillRatings: {
            byPerson: defineProjectQuery(personIDQueryArgsSchema, ({ args, ctx }) => authorizedSkillRatingsQuery(ctx, projectZql)
                .where("personID", args.personID)
                .related("defaultFunction")),
            allRatedAtLeast: defineProjectQuery(skillRatingsAtLeastQueryArgsSchema, ({ args, ctx }) => authorizedSkillRatingsQuery(ctx, projectZql)
                .where("rating", ">=", args.minRating)
                .related("defaultFunction")),
        },
        projectAvailability: {
            byProject: defineProjectQuery({
                input: projectIDQueryArgsSchema,
                description: "Crew availability and booking entries of a project (one per person, with status such as requested, available or booked), oldest first, with the person and their allergy tags. Admins, employees and project collaborators see every entry, other users only their own. Dates are epoch ms.",
            }, ({ args, ctx }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("person", (person) => person.related("personAllergyTags", (assignment) => assignment.related("allergyTag")))
                .orderBy("createdAt", "asc")),
            bookedByPerson: defineProjectQuery(personIDQueryArgsSchema, ({ args, ctx }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("personID", args.personID)
                .where("status", "booked")
                .related("project", (project) => project.where("deletedAt", "IS", null))
                .orderBy("createdAt", "asc")),
            bookedByPeople: defineProjectQuery(personIDsQueryArgsSchema, ({ args, ctx, }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("personID", "IN", args.personIDs)
                .where("status", "booked")
                .related("project")
                .orderBy("createdAt", "asc")),
            pendingPlanning: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("status", "IN", ["unknown", "requested"])
                .related("person")
                .related("project", (project) => project.where("deletedAt", "IS", null))
                .orderBy("createdAt", "asc")),
        },
        projectContacts: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.projectContacts)
                    .where("projectID", args.projectID)
                    .related("person")
                    .orderBy("createdAt", "asc");
            }),
        },
        projectCollaborators: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx }) => authorizedProjectCollaboratorsQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("person", (person) => person.related("companies"))
                .orderBy("createdAt", "asc")),
        },
        crewEmails: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.crewEmails)
                    .where("projectID", args.projectID)
                    .related("recipients", (recipient) => recipient.related("person"))
                    .related("attachments")
                    .orderBy("createdAt", "desc");
            }),
        },
        projectMealPlans: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.projectMealPlans)
                    .where("projectID", args.projectID)
                    .orderBy("planDate", "asc");
            }),
        },
        projectHotelPlans: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.projectHotelPlans)
                    .where("projectID", args.projectID)
                    .orderBy("planDate", "asc");
            }),
        },
        networkCoverageSessions: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.networkCoverageSessions)
                    .where("projectID", args.projectID)
                    .orderBy("startedAt", "desc");
            }),
        },
        projectFunctionSeries: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx }) => authorizedProjectFunctionSeriesQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("projectFunctionGroup")
                .related("days", (day) => day
                .related("assignments", (assignment) => assignment.related("person"))
                .orderBy("functionDate", "asc"))
                .orderBy("name", "asc")),
            listProjectIDs: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedProjectFunctionSeriesQuery(ctx, projectZql).orderBy("projectID", "asc")),
        },
        projectFunctionGroups: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx }) => authorizedProjectFunctionGroupsQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc")),
        },
        projectPersonTravelDays: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx }) => authorizedProjectPersonTravelDaysQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("person")
                .orderBy("travelDate", "asc")),
        },
        functionAssignments: {
            byPerson: defineProjectQuery({
                input: personIDQueryArgsSchema,
                description: "A person's crew function assignments, oldest first, each with its project function and project. Admins and employees can query anyone, other users only themselves plus assignments on projects they collaborate on. Dates are epoch ms.",
            }, ({ args, ctx }) => authorizedFunctionAssignmentsQuery(ctx, projectZql)
                .where("personID", args.personID)
                .related("projectFunction", (projectFunction) => projectFunction.related("project"))
                .orderBy("createdAt", "asc")),
            byPeople: defineProjectQuery(personIDsQueryArgsSchema, ({ args, ctx, }) => authorizedFunctionAssignmentsQuery(ctx, projectZql)
                .where("personID", "IN", args.personIDs)
                .related("projectFunction", (projectFunction) => projectFunction.related("project"))
                .orderBy("createdAt", "asc")),
        },
    };
}
export function createPeoplePlanningMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createDefaultFunctionInputSchema, updateDefaultFunctionInputSchema, defaultFunctionIDInputSchema, upsertSkillRatingInputSchema, deleteSkillRatingInputSchema, upsertProjectAvailabilityInputSchema, batchUpsertProjectAvailabilityInputSchema, deleteProjectAvailabilityInputSchema, updateProjectAvailabilityRemarkInputSchema, updateProjectAvailabilityUnityUsernameInputSchema, createProjectContactInputSchema, updateProjectContactInputSchema, deleteProjectContactInputSchema, createProjectCollaboratorInputSchema, updateProjectCollaboratorInputSchema, deleteProjectCollaboratorInputSchema, upsertProjectMealPlanInputSchema, batchUpsertProjectMealPlansInputSchema, upsertProjectHotelPlanInputSchema, batchUpsertProjectHotelPlansInputSchema, linkNetworkCoverageSessionInputSchema, unlinkNetworkCoverageSessionInputSchema, createProjectFunctionSeriesInputSchema, updateProjectFunctionSeriesInputSchema, deleteProjectFunctionSeriesInputSchema, updateProjectFunctionDayInputSchema, createFunctionAssignmentInputSchema, bulkAssignFunctionSeriesInputSchema, updateFunctionAssignmentInputSchema, deleteFunctionAssignmentInputSchema, initializeProjectFunctionsInputSchema, } = schemas;
    return {
        projectAvailability: {
            upsert: defineProjectMutator({
                input: upsertProjectAvailabilityInputSchema,
                description: "Create or update a person's availability/booking request for a project. Pass a client-generated `id` (reused when updating).",
            }, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const existing = await tx.run(projectZql.projectAvailability
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .one());
                const row = {
                    id: existing?.id ?? args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    status: args.status,
                    availableDays: toZeroDateArray(args.availableDays),
                    notes: args.notes ?? null,
                };
                if (existing) {
                    await tx.mutate.projectAvailability.update(row);
                    return;
                }
                await tx.mutate.projectAvailability.insert(row);
            }),
            batchUpsert: defineProjectMutator(batchUpsertProjectAvailabilityInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const personID of args.personIDs) {
                    const existing = await tx.run(projectZql.projectAvailability
                        .where("projectID", args.projectID)
                        .where("personID", personID)
                        .one());
                    const row = {
                        id: existing?.id ?? crypto.randomUUID(),
                        projectID: args.projectID,
                        personID,
                        status: args.status,
                        availableDays: toZeroDateArray(args.availableDays),
                    };
                    if (existing) {
                        await tx.mutate.projectAvailability.update(row);
                    }
                    else {
                        await tx.mutate.projectAvailability.insert(row);
                    }
                }
            }),
            delete: defineProjectMutator(deleteProjectAvailabilityInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectAvailability", args.id, args.projectID);
                await tx.mutate.projectAvailability.delete({ id: args.id });
            }),
            updateRemark: defineProjectMutator(updateProjectAvailabilityRemarkInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectAvailability", args.id, args.projectID);
                await tx.mutate.projectAvailability.update({
                    id: args.id,
                    crewListRemark: args.crewListRemark ?? null,
                });
            }),
            updateUnityUsername: defineProjectMutator(updateProjectAvailabilityUnityUsernameInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectAvailability", args.id, args.projectID);
                await tx.mutate.projectAvailability.update({
                    id: args.id,
                    unityUsername: args.unityUsername ?? null,
                });
            }),
        },
        projectContacts: {
            create: defineProjectMutator(createProjectContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectContacts.insert({
                    id: args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    description: args.description,
                });
            }),
            update: defineProjectMutator(updateProjectContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectContacts", args.id, args.projectID);
                await tx.mutate.projectContacts.update({
                    id: args.id,
                    ...(args.personID !== undefined ? { personID: args.personID } : {}),
                    ...(args.description !== undefined
                        ? { description: args.description }
                        : {}),
                });
            }),
            delete: defineProjectMutator(deleteProjectContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectContacts", args.id, args.projectID);
                await tx.mutate.projectContacts.delete({ id: args.id });
            }),
        },
        projectCollaborators: {
            create: defineProjectMutator(createProjectCollaboratorInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectCollaboratorManagementAccess(ctx);
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const person = await tx.run(projectZql.people.where("id", args.personID).one());
                if (!person)
                    throw new NotFoundError("Person not found");
                if (!COLLABORATOR_PERSON_TYPES.includes(person.personType)) {
                    throw new ForbiddenError("Only employees and freelancers can be project collaborators");
                }
                await tx.mutate.projectCollaborators.insert({
                    id: args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    canEdit: args.canEdit ?? false,
                });
            }),
            update: defineProjectMutator(updateProjectCollaboratorInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectCollaboratorManagementAccess(ctx);
                await assertProjectCollaboratorInProject(tx, projectZql, args.id, args.projectID);
                await tx.mutate.projectCollaborators.update({
                    id: args.id,
                    ...(args.canEdit !== undefined ? { canEdit: args.canEdit } : {}),
                });
            }),
            delete: defineProjectMutator(deleteProjectCollaboratorInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectCollaboratorManagementAccess(ctx);
                await assertProjectCollaboratorInProject(tx, projectZql, args.id, args.projectID);
                await tx.mutate.projectCollaborators.delete({ id: args.id });
            }),
        },
        projectMealPlans: {
            upsert: defineProjectMutator(upsertProjectMealPlanInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const planDate = toZeroDate(args.planDate) ?? 0;
                const existing = await tx.run(projectZql.projectMealPlans
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .where("planDate", planDate)
                    .one());
                const row = {
                    id: existing?.id ?? args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    planDate,
                    lunch: args.lunch,
                    dinner: args.dinner,
                };
                if (existing) {
                    await tx.mutate.projectMealPlans.update(row);
                }
                else {
                    await tx.mutate.projectMealPlans.insert(row);
                }
            }),
            batchUpsert: defineProjectMutator(batchUpsertProjectMealPlansInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const plan of args.plans) {
                    const planDate = toZeroDate(plan.planDate) ?? 0;
                    const existing = await tx.run(projectZql.projectMealPlans
                        .where("projectID", args.projectID)
                        .where("personID", plan.personID)
                        .where("planDate", planDate)
                        .one());
                    const row = {
                        id: existing?.id ?? plan.id,
                        projectID: args.projectID,
                        personID: plan.personID,
                        planDate,
                        lunch: plan.lunch,
                        dinner: plan.dinner,
                    };
                    if (existing) {
                        await tx.mutate.projectMealPlans.update(row);
                    }
                    else {
                        await tx.mutate.projectMealPlans.insert(row);
                    }
                }
            }),
        },
        projectHotelPlans: {
            upsert: defineProjectMutator(upsertProjectHotelPlanInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const planDate = toZeroDate(args.planDate) ?? 0;
                const existing = await tx.run(projectZql.projectHotelPlans
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .where("planDate", planDate)
                    .one());
                const row = {
                    id: existing?.id ?? args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    planDate,
                    needsHotel: args.needsHotel,
                    roomType: args.needsHotel ? (args.roomType ?? "single") : "single",
                    roommatePersonID: args.needsHotel && args.roomType === "double"
                        ? (args.roommatePersonID ?? null)
                        : null,
                };
                if (existing) {
                    await tx.mutate.projectHotelPlans.update(row);
                }
                else {
                    await tx.mutate.projectHotelPlans.insert(row);
                }
            }),
            batchUpsert: defineProjectMutator(batchUpsertProjectHotelPlansInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const plan of args.plans) {
                    const planDate = toZeroDate(plan.planDate) ?? 0;
                    const existing = await tx.run(projectZql.projectHotelPlans
                        .where("projectID", args.projectID)
                        .where("personID", plan.personID)
                        .where("planDate", planDate)
                        .one());
                    const row = {
                        id: existing?.id ?? plan.id,
                        projectID: args.projectID,
                        personID: plan.personID,
                        planDate,
                        needsHotel: plan.needsHotel,
                        roomType: plan.needsHotel
                            ? (plan.roomType ?? "single")
                            : "single",
                        roommatePersonID: plan.needsHotel && plan.roomType === "double"
                            ? (plan.roommatePersonID ?? null)
                            : null,
                    };
                    if (existing) {
                        await tx.mutate.projectHotelPlans.update(row);
                    }
                    else {
                        await tx.mutate.projectHotelPlans.insert(row);
                    }
                }
            }),
        },
        networkCoverageSessions: {
            link: defineProjectMutator(linkNetworkCoverageSessionInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.networkCoverageSessions.insert({
                    id: args.id,
                    projectID: args.projectID,
                    externalSessionID: args.externalSessionID,
                    deviceName: args.deviceName ?? null,
                    deviceUID: args.deviceUID ?? null,
                    startedAt: args.startedAt,
                    endedAt: args.endedAt ?? null,
                    hasGpx: args.hasGpx ?? false,
                    summaryStats: args.summaryStats ?? null,
                });
            }),
            unlink: defineProjectMutator(unlinkNetworkCoverageSessionInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "networkCoverageSessions", args.id, args.projectID);
                await tx.mutate.networkCoverageSessions.delete({ id: args.id });
            }),
        },
        defaultFunctionGroups: {
            create: defineProjectMutator(createDefaultFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctionGroups.insert({
                    ...args,
                    locationType: args.locationType ?? null,
                    displayOrder: args.displayOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateDefaultFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctionGroups.update(args);
            }),
            delete: defineProjectMutator(defaultFunctionGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctionGroups.delete({ id: args.id });
            }),
        },
        defaultFunctions: {
            create: defineProjectMutator(createDefaultFunctionInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctions.insert({
                    ...args,
                    defaultFunctionGroupID: args.defaultFunctionGroupID ?? null,
                    displayOrder: args.displayOrder ?? 0,
                    systemTag: args.systemTag ?? null,
                    locationType: args.locationType ?? null,
                    showOnInitialize: args.showOnInitialize ?? true,
                });
            }),
            update: defineProjectMutator(updateDefaultFunctionInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctions.update(args);
            }),
            delete: defineProjectMutator(defaultFunctionIDInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctions.delete({ id: args.id });
            }),
        },
        personSkillRatings: {
            upsert: defineProjectMutator(upsertSkillRatingInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                const existing = await tx.run(projectZql.personSkillRatings
                    .where("personID", args.personID)
                    .where("defaultFunctionID", args.defaultFunctionID)
                    .one());
                if (existing) {
                    await tx.mutate.personSkillRatings.update({
                        id: existing.id,
                        rating: args.rating,
                        notes: args.notes ?? null,
                    });
                    return;
                }
                await tx.mutate.personSkillRatings.insert({
                    ...args,
                    notes: args.notes ?? null,
                });
            }),
            delete: defineProjectMutator(deleteSkillRatingInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.personSkillRatings.delete({ id: args.id });
            }),
        },
        projectFunctionGroups: {
            create: defineProjectMutator(createProjectFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectFunctionGroups.insert({
                    ...args,
                    locationType: args.locationType ?? null,
                    displayOrder: args.displayOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateProjectFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectFunctionGroups", args.id, args.projectID);
                await tx.mutate.projectFunctionGroups.update(args);
            }),
            delete: defineProjectMutator(deleteProjectFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectFunctionGroups", args.id, args.projectID);
                await tx.mutate.projectFunctionGroups.delete({ id: args.id });
            }),
        },
        projectFunctionSeries: {
            create: defineProjectMutator(createProjectFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const selectedDays = normalizeZeroDateArray(args.selectedDays);
                const requiredCount = args.requiredCount ?? 1;
                await tx.mutate.projectFunctionSeries.insert({
                    id: args.id,
                    projectID: args.projectID,
                    projectFunctionGroupID: args.projectFunctionGroupID ?? null,
                    name: args.name,
                    requiredCount,
                    selectedDays,
                    systemTag: args.systemTag ?? null,
                    locationType: args.locationType ?? null,
                });
                await syncProjectFunctionSeriesDays(tx, {
                    projectID: args.projectID,
                    seriesID: args.id,
                    name: args.name,
                    requiredCount,
                    selectedDays,
                    dayIDsByDate: args.dayIDsByDate,
                });
            }),
            update: defineProjectMutator(updateProjectFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const existing = await tx.run(projectZql.projectFunctionSeries.where("id", args.id).one());
                if (!existing || existing.projectID !== args.projectID) {
                    throw new NotFoundError("Function series not found");
                }
                const selectedDays = args.selectedDays === undefined
                    ? (existing.selectedDays ?? [])
                    : normalizeZeroDateArray(args.selectedDays);
                const name = args.name ?? existing.name;
                const requiredCount = args.requiredCount ?? existing.requiredCount;
                await tx.mutate.projectFunctionSeries.update({
                    id: args.id,
                    ...(args.projectFunctionGroupID !== undefined
                        ? { projectFunctionGroupID: args.projectFunctionGroupID ?? null }
                        : {}),
                    ...(args.name !== undefined ? { name } : {}),
                    ...(args.requiredCount !== undefined ? { requiredCount } : {}),
                    ...(args.selectedDays !== undefined ? { selectedDays } : {}),
                    ...(args.systemTag !== undefined
                        ? { systemTag: args.systemTag ?? null }
                        : {}),
                    ...(args.locationType !== undefined
                        ? { locationType: args.locationType ?? null }
                        : {}),
                });
                await syncProjectFunctionSeriesDays(tx, {
                    projectID: args.projectID,
                    seriesID: args.id,
                    name,
                    requiredCount,
                    selectedDays,
                });
            }),
            delete: defineProjectMutator(deleteProjectFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectFunctionSeries", args.id, args.projectID);
                await tx.mutate.projectFunctionSeries.delete({ id: args.id });
            }),
            updateDay: defineProjectMutator(updateProjectFunctionDayInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectFunctions", args.id, args.projectID);
                await tx.mutate.projectFunctions.update({
                    id: args.id,
                    requiredCountOverride: args.requiredCountOverride,
                });
            }),
            initialize: defineProjectMutator(initializeProjectFunctionsInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const fn of args.functions) {
                    const selectedDays = normalizeZeroDateArray(fn.selectedDays);
                    await tx.mutate.projectFunctionSeries.insert({
                        id: fn.id,
                        projectID: args.projectID,
                        projectFunctionGroupID: fn.projectFunctionGroupID ?? null,
                        name: fn.name,
                        requiredCount: fn.requiredCount,
                        selectedDays,
                        systemTag: fn.systemTag ?? null,
                        locationType: fn.locationType ?? null,
                    });
                    await syncProjectFunctionSeriesDays(tx, {
                        projectID: args.projectID,
                        seriesID: fn.id,
                        name: fn.name,
                        requiredCount: fn.requiredCount,
                        selectedDays,
                    });
                    const overridesByDate = new Map(fn.dayOverrides.map((override) => [
                        toZeroDate(override.date),
                        override.count,
                    ]));
                    if (overridesByDate.size === 0)
                        continue;
                    const days = await tx.run(projectZql.projectFunctions.where("seriesID", fn.id));
                    for (const day of days ?? []) {
                        if (!overridesByDate.has(day.functionDate))
                            continue;
                        await tx.mutate.projectFunctions.update({
                            id: day.id,
                            requiredCountOverride: overridesByDate.get(day.functionDate),
                        });
                    }
                }
            }),
        },
        projectPersonTravelDays: {
            upsert: defineProjectMutator(upsertProjectPersonTravelDayInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const travelDate = toZeroDate(args.travelDate);
                const existing = await tx.run(projectZql.projectPersonTravelDays
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .where("travelDate", travelDate)
                    .one());
                if (existing) {
                    await tx.mutate.projectPersonTravelDays.update({
                        id: existing.id,
                        remark: args.remark ?? null,
                    });
                    return;
                }
                await tx.mutate.projectPersonTravelDays.insert({
                    id: args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    travelDate,
                    remark: args.remark ?? null,
                });
            }),
            delete: defineProjectMutator(deleteProjectPersonTravelDayInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectRowWriteAccess(tx, ctx, projectZql, "projectPersonTravelDays", args.id, args.projectID);
                await tx.mutate.projectPersonTravelDays.delete({ id: args.id });
            }),
        },
        functionAssignments: {
            create: defineProjectMutator({
                input: createFunctionAssignmentInputSchema,
                description: "Book a person for a crew function on a project. Pass a client-generated `id` plus `functionID`, `personID` and `projectID`.",
            }, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await assertProjectFunctionsInProject(tx, ctx, projectZql, args.projectID, [args.functionID]);
                await tx.mutate.functionAssignments.insert({
                    id: args.id,
                    functionID: args.functionID,
                    personID: args.personID,
                    remark: args.remark ?? null,
                });
            }),
            bulkAssignSeries: defineProjectMutator(bulkAssignFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await assertProjectFunctionsInProject(tx, ctx, projectZql, args.projectID, args.functionIDs);
                for (const functionID of args.functionIDs) {
                    const existing = await tx.run(projectZql.functionAssignments
                        .where("functionID", functionID)
                        .where("personID", args.personID)
                        .one());
                    if (existing)
                        continue;
                    await tx.mutate.functionAssignments.insert({
                        id: crypto.randomUUID(),
                        functionID,
                        personID: args.personID,
                        remark: null,
                    });
                }
            }),
            update: defineProjectMutator(updateFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
                await assertFunctionAssignmentWriteAccess(tx, ctx, projectZql, args.id, args.projectID);
                await tx.mutate.functionAssignments.update({
                    id: args.id,
                    remark: args.remark ?? null,
                });
            }),
            delete: defineProjectMutator({
                input: deleteFunctionAssignmentInputSchema,
                description: "Remove a crew booking (function assignment).",
            }, async ({ tx, ctx, args, }) => {
                await assertFunctionAssignmentWriteAccess(tx, ctx, projectZql, args.id, args.projectID);
                await tx.mutate.functionAssignments.delete({ id: args.id });
            }),
        },
    };
}
export const peoplePlanningDomainDescriptions = describeDomains({
    crewEmails: "Emails sent to the crew of a project",
    defaultFunctions: "Standard crew functions (roles) such as camera operator",
    defaultFunctionGroups: "Groups of standard crew functions",
    functionAssignments: "Crew bookings: which person fills which function on which day",
    networkCoverageSessions: "Mobile network coverage measurements linked to projects",
    personSkillRatings: "Skill ratings of people per function",
    projectAvailability: "Crew availability and booking requests per project and day",
    projectContacts: "External contacts of a project",
    projectCollaborators: "Freelancers added to a project, optionally with edit access",
    projectFunctionGroups: "Function groups used in a project crew plan",
    projectFunctionSeries: "Crew functions needed on a project over a series of days",
    projectHotelPlans: "Hotel needs per person and night on a project",
    projectMealPlans: "Meal plans per person and day on a project",
    projectPersonTravelDays: "Travel days per person on a project",
});
